<div class="p-4 h-full flex flex-col">
  <div class="flex items-center gap-4 mb-3 flex-wrap">
    <h1 class="text-xl font-semibold dark:text-white">Exchange Agreement Review</h1>
    <span class="text-xs text-gray-500 dark:text-gray-400">
      Approve grants live data (Cognito <code>custom:live_data_access</code> flip); decline revokes it and reverts to delayed-data mode.
      All decisions land in <code>F2-ADMIN.ReviewAudit</code>. Panel updates live from any admin.
    </span>
    <!-- IT-F2-426 c/2795820a (Mike 2026-10-02): Live toggle. On by
         default; flipping off closes the SSE stream so the table
         doesn't re-render under the operator while they review. -->
    <label class="ml-auto flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none"
           title="Live updates stream approve/decline/entitlement events from the server and refresh the queue. Turn off to freeze the view while you review.">
      <input type="checkbox" class="w-4 h-4 dark-checkbox"
             [checked]="live_updates"
             (change)="on_live_toggle_change($any($event.target).checked)" />
      <i class="fa-solid fa-circle text-[8px]"
         [ngClass]="live_updates ? 'text-emerald-500 animate-pulse' : 'text-gray-400'"></i>
      Live
    </label>
    <button type="button"
            class="dark-btn text-xs px-2 py-1"
            (click)="load()"
            [disabled]="loading"
            title="Refresh the review list">
      <i class="fa-solid fa-arrows-rotate mr-1"></i> Refresh
    </button>
    <button type="button"
            class="dark-btn text-xs px-2 py-1"
            (click)="export_csv()"
            [disabled]="loading || review_rows.length === 0"
            title="Download the currently filtered + sorted rows as CSV">
      <i class="fa-solid fa-file-arrow-down mr-1"></i> Export CSV
    </button>
    <!-- IT-F2-426 c/e12385a8 (Mike 2026-10-02): navigate to the full
         standard-questionnaire page (same form shape as the regular
         user flow). PDF upload prefills the fields. Admin trust-
         approves on create. -->
    <a routerLink="/admin/exchange-agreement-review/add-from-pdf"
       class="dark-btn text-xs px-2 py-1 bg-blue-600 text-white"
       title="Create a new Exchange_Agreements row from a signed PDF — opens the full standard questionnaire with fields prefilled from the PDF.">
      <i class="fa-regular fa-file-pdf mr-1"></i> Add Agreement from PDF
    </a>
  </div>

  <!-- Filter + count row -->
  <div class="flex items-center gap-3 mb-2 flex-wrap">
    <label class="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
      Filter:
      <select class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs"
              [ngModel]="review_filter"
              (ngModelChange)="on_review_filter_change($event)">
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="declined">Declined</option>
        <option value="all">All</option>
      </select>
    </label>
    <label class="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
      Search:
      <input type="text"
             class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs w-64"
             [ngModel]="search_query"
             (ngModelChange)="on_search_change($event)"
             placeholder="email / name / customer / user id" />
      <button *ngIf="search_query"
              type="button"
              class="text-[11px] underline text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              (click)="on_search_change('')"
              title="Clear search">clear</button>
    </label>
    <span class="text-gray-500 dark:text-gray-400 text-xs">
      {{ review_rows.length }} {{ review_rows.length === 1 ? 'row' : 'rows' }}
    </span>
    <span *ngIf="pending_count > 0"
          class="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-500 text-white"
          title="Total pending reviews across all filters">
      {{ pending_count }} pending
    </span>
    <span *ngIf="loading" class="text-xs text-gray-500 dark:text-gray-400">
      <i class="fa-solid fa-spinner fa-spin mr-1"></i> Loading…
    </span>
  </div>

  <!-- Bulk action bar (cid b1e2ecd2, IT-F2-391). Sits above the table
       and holds Approve as Pro / Approve as Non-Pro / Decline. Buttons
       are disabled when no rows are selected OR when no selected row
       would actually change state on the target action (e.g. all
       selected rows are already Approved-as-Pro when Approve-as-Pro is
       clicked). The reason input applies to bulk-decline; single-row
       decline from the View modal still uses its own per-row reason. -->
  <div class="flex items-center gap-2 mb-3 flex-wrap p-2 rounded border border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-blue-100/20">
    <span class="text-xs text-gray-700 dark:text-gray-200 font-medium">
      {{ selection_set.size }} selected
    </span>
    <span *ngIf="selection_set.size > 0"
          class="text-[11px] text-gray-500 dark:text-gray-400">
      · Approve as Pro affects {{ effective_approve_pro_count() }},
      Approve as Non-Pro affects {{ effective_approve_non_pro_count() }},
      Decline affects {{ effective_decline_count() }} user(s)
    </span>
    <span *ngIf="selection_set.size > 0" class="text-[11px] text-gray-400">
      <button type="button"
              class="underline hover:text-gray-600 dark:hover:text-gray-200"
              (click)="clear_selection()"
              [disabled]="bulk_busy">Clear selection</button>
    </span>
    <input class="ml-2 flex-1 min-w-[160px] max-w-[360px] border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs"
           [(ngModel)]="bulk_reason"
           [disabled]="bulk_busy"
           placeholder="Reason (required to Decline; optional for Approve)" />
    <button type="button"
            class="dark-btn text-xs px-2 py-1 bg-blue-600 text-white disabled:opacity-50"
            (click)="bulk_approve_as_pro()"
            [disabled]="bulk_approve_pro_disabled()"
            [title]="bulk_approve_pro_disabled() && !bulk_busy ? (selection_set.size === 0 ? 'Select at least one row' : 'All selected rows are already Approved as Pro') : ('Approve ' + effective_approve_pro_count() + ' selected as Pro')">
      Approve as Pro
      <span *ngIf="selection_set.size > 0" class="ml-1 text-[10px] opacity-80">
        ({{ effective_approve_pro_count() }})
      </span>
    </button>
    <button type="button"
            class="dark-btn text-xs px-2 py-1 bg-green-600 text-white disabled:opacity-50"
            (click)="bulk_approve_as_non_pro()"
            [disabled]="bulk_approve_non_pro_disabled()"
            [title]="bulk_approve_non_pro_disabled() && !bulk_busy ? (selection_set.size === 0 ? 'Select at least one row' : 'All selected rows are already Approved as Non-Pro') : ('Approve ' + effective_approve_non_pro_count() + ' selected as Non-Pro')">
      Approve as Non-Pro
      <span *ngIf="selection_set.size > 0" class="ml-1 text-[10px] opacity-80">
        ({{ effective_approve_non_pro_count() }})
      </span>
    </button>
    <button type="button"
            class="dark-btn text-xs px-2 py-1 text-red-500 disabled:opacity-50"
            (click)="bulk_decline()"
            [disabled]="bulk_decline_disabled()"
            [title]="bulk_decline_disabled() && !bulk_busy ? (selection_set.size === 0 ? 'Select at least one row' : (!bulk_reason.trim() ? 'Type a reason first' : 'All selected rows are already Declined')) : ('Decline ' + effective_decline_count() + ' selected — REVOKES live data on approved rows')">
      Decline
      <span *ngIf="selection_set.size > 0" class="ml-1 text-[10px] opacity-80">
        ({{ effective_decline_count() }})
      </span>
    </button>
    <!-- IT-F2-391 2026-09-15 (Mike c/ff3ca265): bulk Re-eval iterates
         selected rows through the /admin/reeval endpoint — no preview
         count because we can't know before hitting the backend whether
         each row's classification will actually flip. Toast copy per
         row surfaces the diff (or "no change"). -->
    <button type="button"
            class="dark-btn text-xs px-2 py-1 text-gray-700 dark:text-gray-200 disabled:opacity-50"
            (click)="bulk_reeval()"
            [disabled]="bulk_reeval_disabled()"
            [title]="bulk_reeval_disabled() && !bulk_busy ? 'Select at least one row' : ('Re-derive Pro status from stored answers on ' + selection_set.size + ' selected row(s)')">
      <i class="fa-solid fa-arrows-rotate mr-1"></i> Re-eval
      <span *ngIf="selection_set.size > 0" class="ml-1 text-[10px] opacity-80">
        ({{ selection_set.size }})
      </span>
    </button>
    <span *ngIf="bulk_busy" class="text-xs text-gray-500 dark:text-gray-400">
      <i class="fa-solid fa-spinner fa-spin mr-1"></i> Applying…
    </span>
  </div>

  <!-- IT-F2-413 c/90494e0c: swapped plain <table> for ag-grid with the
       same features as the admin panel Users grid — per-column
       floating filters, sortable headers, multi-row selection with
       filtered-scoped "select all", row-count status bar, CSV export.
       Legacy top toolbar (filter select + free-text search + bulk
       action bar) stays — they layer BEFORE ag-grid's per-column
       filters and remain useful for the common "just the pending
       queue" scan. -->
  <div class="flex-1 w-full h-[600px] relative">
    <ag-grid-angular
      class="w-full h-full"
      [theme]="theme"
      [gridOptions]="grid.options"
      [columnDefs]="grid.columnDefs"
      [rowData]="review_rows"
      [statusBar]="grid.statusBar"
    />
  </div>
</div>

<!-- View Application modal — shows the full submitted agreement so the
     admin can decide informed. Approve/Decline actions are inline in the
     footer so the admin can act without going back to the table. Fields
     live under r.APPLICATION_DETAIL (nested projection from backend
     _agreement_to_exhibit_b_row v32) plus the flat SUBSCRIBERS_* /
     EMPLOYERS_* columns already on the row. -->
<div *ngIf="view_agreement_open && view_agreement_row"
     class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
     (click)="close_view_application()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
      <div>
        <h3 class="font-semibold text-gray-900 dark:text-white">
          {{ view_agreement_row.SUBSCRIBERS_FIRST_NAME }} {{ view_agreement_row.SUBSCRIBERS_LAST_NAME }}
        </h3>
        <div class="text-[11px] text-gray-600 dark:text-gray-300">
          {{ view_agreement_row.SUBSCRIBERS_EMAIL_ADDRESS || '—' }}
          &nbsp;·&nbsp; Customer: <b>{{ view_agreement_row.CUSTOMER || '—' }}</b>
          &nbsp;·&nbsp; Submitted: {{ (view_agreement_row.DATE_AND_TIME_STAMP || view_agreement_row.EARLIEST_DATE) ? ((view_agreement_row.DATE_AND_TIME_STAMP || view_agreement_row.EARLIEST_DATE) | date:'yyyy-MM-dd HH:mm:ss z') : '—' }}
        </div>
      </div>
      <span class="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
            [ngClass]="status_chip_for(view_agreement_row).classes">
        {{ status_chip_for(view_agreement_row).label }}
      </span>
    </div>

    <!-- Submission history nav (IT-F2-391 Mike c/97b0c021).
         Shows only when the row has prior submissions. Older ← / →
         Newer flips the modal body between current and historical
         snapshots. -->
    <div *ngIf="has_prior_submissions()"
         class="px-4 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-blue-100/10 flex items-center gap-2 text-[12px]">
      <button type="button"
              class="dark-btn text-xs px-2 py-0.5 disabled:opacity-40"
              (click)="go_prev_submission()"
              [disabled]="!can_go_prev_submission()"
              title="View older submission">← Older</button>
      <span class="font-medium text-gray-700 dark:text-gray-200">
        {{ submission_attempt_label() }}
      </span>
      <span *ngIf="submission_history_index > 0"
            class="inline-block px-2 py-0.5 rounded text-[11px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
            title="Read-only historical snapshot — approve/decline is disabled">
        History (read-only)
      </span>
      <span *ngIf="history_loading"
            class="text-[11px] text-gray-500 dark:text-gray-400 italic">
        <i class="fa-solid fa-spinner fa-spin mr-1"></i>Loading history…
      </span>
      <button type="button"
              class="dark-btn text-xs px-2 py-0.5 disabled:opacity-40 ml-auto"
              (click)="go_next_submission()"
              [disabled]="!can_go_next_submission()"
              title="View newer submission">Newer →</button>
    </div>

    <!-- Historical snapshot body — simplified read-only view. Renders when
         submission_history_index > 0. Approve/decline hidden in the footer
         via the same guard. -->
    <div *ngIf="submission_history_index > 0"
         class="p-4 space-y-4 text-sm text-gray-800 dark:text-gray-200">
      <div class="text-[11px] text-gray-500 dark:text-gray-400 italic">
        Snapshot of a prior submission by this user. Fields shown reflect
        what the user submitted at that time; the decision recorded then
        is shown at the bottom.
      </div>
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Contact</h4>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div><span class="text-gray-500">Name:</span> {{ displayed_submission()?.first_name || '—' }} {{ displayed_submission()?.last_name || '' }}</div>
          <div><span class="text-gray-500">Email:</span> {{ displayed_submission()?.email || '—' }}</div>
          <div><span class="text-gray-500">Phone:</span> {{ displayed_submission()?.phone || '—' }}</div>
          <div><span class="text-gray-500">Outside US:</span> {{ render_yes_no(displayed_submission()?.outside_us) }}</div>
          <div class="col-span-2"><span class="text-gray-500">Street:</span> {{ displayed_submission()?.address || '—' }}</div>
          <div><span class="text-gray-500">City:</span> {{ displayed_submission()?.city || '—' }}</div>
          <div><span class="text-gray-500">State:</span> {{ displayed_submission()?.state || '—' }}</div>
          <div><span class="text-gray-500">Zip:</span> {{ displayed_submission()?.zip_code || '—' }}</div>
          <div><span class="text-gray-500">Country:</span> {{ displayed_submission()?.country || '—' }}</div>
        </div>
      </section>
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Employer</h4>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div class="col-span-2"><span class="text-gray-500">Name:</span> {{ displayed_submission()?.employer?.name || '—' }}</div>
          <div class="col-span-2"><span class="text-gray-500">Address:</span> {{ displayed_submission()?.employer?.address || '—' }}</div>
          <div><span class="text-gray-500">City:</span> {{ displayed_submission()?.employer?.city || '—' }}</div>
          <div><span class="text-gray-500">State:</span> {{ displayed_submission()?.employer?.state || '—' }}</div>
          <div><span class="text-gray-500">Zip:</span> {{ displayed_submission()?.employer?.zip_code || '—' }}</div>
          <div><span class="text-gray-500">Country:</span> {{ displayed_submission()?.employer?.country || '—' }}</div>
        </div>
      </section>
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Employment</h4>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div><span class="text-gray-500">Status:</span> {{ displayed_submission()?.employment_status || '—' }}</div>
          <div><span class="text-gray-500">Occupation:</span> {{ displayed_submission()?.occupation || '—' }}</div>
          <div><span class="text-gray-500">Job title:</span> {{ displayed_submission()?.job_title || '—' }}</div>
          <div><span class="text-gray-500">Function:</span> {{ displayed_submission()?.employment_function || '—' }}</div>
        </div>
      </section>
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Question Answers</h4>
        <ul class="space-y-1 text-[13px]">
          <li *ngFor="let q of agreement_question_labels" class="flex items-start justify-between border-b border-gray-100 dark:border-[#434863]/50 py-1">
            <span class="text-gray-700 dark:text-gray-300 pr-3">{{ q.label }}</span>
            <span class="font-medium whitespace-nowrap"
                  [ngClass]="{
                    'text-green-600 dark:text-green-400': render_yes_no(displayed_submission()?.questions?.[q.key]) === 'Yes',
                    'text-red-600 dark:text-red-400':   render_yes_no(displayed_submission()?.questions?.[q.key]) === 'No',
                    'text-gray-500': render_yes_no(displayed_submission()?.questions?.[q.key]) === '—'
                  }">
              {{ render_yes_no(displayed_submission()?.questions?.[q.key]) }}
            </span>
          </li>
        </ul>
      </section>
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Decision at the Time</h4>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div><span class="text-gray-500">Status:</span> {{ displayed_submission()?.review_status || 'unreviewed' }}</div>
          <div><span class="text-gray-500">Pro/Non-Pro:</span> {{ displayed_submission()?.pro_access ? 'Pro' : 'Non-Pro' }}</div>
          <div><span class="text-gray-500">Reviewer:</span> {{ displayed_submission()?.review_actor_email || '—' }}</div>
          <div><span class="text-gray-500">Decided at:</span> {{ displayed_submission()?.review_action_at ? (displayed_submission()?.review_action_at | date:'yyyy-MM-dd HH:mm:ss z') : '—' }}</div>
          <div class="col-span-2"><span class="text-gray-500">Reason:</span> {{ displayed_submission()?.review_reason || '—' }}</div>
          <div class="col-span-2"><span class="text-gray-500">Submitted at:</span> {{ displayed_submission()?.submitted_at ? (displayed_submission()?.submitted_at | date:'yyyy-MM-dd HH:mm:ss z') : '—' }}</div>
        </div>
      </section>
    </div>

    <div *ngIf="submission_history_index === 0"
         class="p-4 space-y-4 text-sm text-gray-800 dark:text-gray-200">

      <!-- Contact / Address -->
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Contact & Address</h4>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div><span class="text-gray-500">Phone:</span> {{ view_agreement_row.SUBSCRIBERS_TELEPHONE_NUMBER || '—' }}</div>
          <div><span class="text-gray-500">Outside US:</span> {{ render_yes_no(view_agreement_row.APPLICATION_DETAIL?.outside_us) }}</div>
          <div class="col-span-2"><span class="text-gray-500">Street:</span> {{ view_agreement_row.APPLICATION_DETAIL?.address || '—' }}</div>
          <div><span class="text-gray-500">City:</span> {{ view_agreement_row.APPLICATION_DETAIL?.city || '—' }}</div>
          <div><span class="text-gray-500">State:</span> {{ view_agreement_row.APPLICATION_DETAIL?.state || '—' }}</div>
          <div><span class="text-gray-500">Zip:</span> {{ view_agreement_row.APPLICATION_DETAIL?.zip_code || '—' }}</div>
          <div><span class="text-gray-500">Country:</span> {{ view_agreement_row.APPLICATION_DETAIL?.country || view_agreement_row.SUBSCRIBERS_COUNTRY || '—' }}</div>
        </div>
      </section>

      <!-- Employer -->
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Employer</h4>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div class="col-span-2"><span class="text-gray-500">Name:</span> {{ view_agreement_row.EMPLOYERS_NAME || '—' }}</div>
          <div class="col-span-2"><span class="text-gray-500">Address:</span> {{ view_agreement_row.EMPLOYERS_ADDRESS || '—' }}</div>
          <div><span class="text-gray-500">City:</span> {{ view_agreement_row.EMPLOYERS_CITY || '—' }}</div>
          <div><span class="text-gray-500">State:</span> {{ view_agreement_row.EMPLOYERS_STATE || '—' }}</div>
          <div><span class="text-gray-500">Zip:</span> {{ view_agreement_row.EMPLOYERS_ZIP_CODE || '—' }}</div>
          <div><span class="text-gray-500">Country:</span> {{ view_agreement_row.EMPLOYERS_COUNTRY || '—' }}</div>
          <div><span class="text-gray-500">Employer outside US:</span> {{ render_yes_no(view_agreement_row.APPLICATION_DETAIL?.employer_outside_us) }}</div>
        </div>
      </section>

      <!-- Employment -->
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Employment</h4>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <div><span class="text-gray-500">Status:</span> {{ view_agreement_row.APPLICATION_DETAIL?.employment_status || '—' }}</div>
          <div><span class="text-gray-500">Occupation:</span> {{ view_agreement_row.SUBSCRIBERS_OCCUPATION || '—' }}</div>
          <div><span class="text-gray-500">Job title:</span> {{ view_agreement_row['SUBSCRIBERS_TITLE/POSITION'] || '—' }}</div>
          <div><span class="text-gray-500">Function:</span> {{ view_agreement_row.SUBSCRIBERS_EMPLOYMENT_FUNCTION || '—' }}</div>
          <div class="col-span-2"><span class="text-gray-500">Other:</span> {{ view_agreement_row.APPLICATION_DETAIL?.other_employment || '—' }}</div>
        </div>
      </section>

      <!-- Questions -->
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Question Answers</h4>
        <ul class="space-y-1 text-[13px]">
          <li *ngFor="let q of agreement_question_labels" class="flex items-start justify-between border-b border-gray-100 dark:border-[#434863]/50 py-1">
            <span class="text-gray-700 dark:text-gray-300 pr-3">{{ q.label }}</span>
            <span class="font-medium whitespace-nowrap"
                  [ngClass]="{
                    'text-green-600 dark:text-green-400': render_yes_no(view_agreement_row.APPLICATION_DETAIL?.questions?.[q.key]) === 'Yes',
                    'text-red-600 dark:text-red-400':   render_yes_no(view_agreement_row.APPLICATION_DETAIL?.questions?.[q.key]) === 'No',
                    'text-gray-500': render_yes_no(view_agreement_row.APPLICATION_DETAIL?.questions?.[q.key]) === '—'
                  }">
              {{ render_yes_no(view_agreement_row.APPLICATION_DETAIL?.questions?.[q.key]) }}
            </span>
          </li>
        </ul>
      </section>

      <!-- Agreements -->
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Agreements Accepted</h4>
        <ul class="space-y-1 text-[13px]">
          <li><span class="text-gray-500">Forms:</span> {{ view_agreement_row.APPLICATION_DETAIL?.agree_to_forms ? 'Yes' : 'No' }}</li>
          <li><span class="text-gray-500">Terms:</span> {{ view_agreement_row.APPLICATION_DETAIL?.agree_to_terms ? 'Yes' : 'No' }}</li>
          <li><span class="text-gray-500">Notify:</span> {{ view_agreement_row.APPLICATION_DETAIL?.agree_to_notify ? 'Yes' : 'No' }}</li>
          <li><span class="text-gray-500">Signature:</span> {{ view_agreement_row.APPLICATION_DETAIL?.agree_to_signature ? 'Yes' : 'No' }}</li>
          <li><span class="text-gray-500">Electronic signature:</span> <span class="font-mono">{{ view_agreement_row.APPLICATION_DETAIL?.electronic_signature || '—' }}</span></li>
        </ul>
      </section>

      <!-- IT-F2-426 c/f4081b4b (Mike 2026-10-02): signed-PDF attachment.
           One PDF per agreement; re-upload replaces. Backend streams to
           GridFS (F2-ADMIN.exchange_agreement_pdfs); row.SIGNED_PDF
           carries the metadata. Hidden when viewing a historical snapshot
           (attachments bind to the current agreement row, not prior
           submissions). -->
      <section *ngIf="submission_history_index === 0">
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Signed PDF</h4>
        <div *ngIf="view_agreement_row.SIGNED_PDF; else uploadPdfBlock"
             class="flex items-center gap-3 flex-wrap p-2 rounded border border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-blue-100/10 text-[12px]">
          <i class="fa-regular fa-file-pdf text-red-500"></i>
          <span class="font-mono">{{ view_agreement_row.SIGNED_PDF.filename }}</span>
          <span class="text-gray-500">{{ format_pdf_size(view_agreement_row.SIGNED_PDF.size) }}</span>
          <span class="text-gray-500" *ngIf="view_agreement_row.SIGNED_PDF.uploaded_at">
            uploaded {{ view_agreement_row.SIGNED_PDF.uploaded_at | date:'yyyy-MM-dd HH:mm' }}
            <span *ngIf="view_agreement_row.SIGNED_PDF.uploaded_by"> by {{ view_agreement_row.SIGNED_PDF.uploaded_by }}</span>
          </span>
          <div class="ml-auto flex items-center gap-2">
            <a class="dark-btn text-xs px-2 py-1"
               [href]="signed_pdf_url(view_agreement_row, false)"
               target="_blank" rel="noopener">
              <i class="fa-solid fa-eye mr-1"></i> View
            </a>
            <a class="dark-btn text-xs px-2 py-1"
               [href]="signed_pdf_url(view_agreement_row, true)">
              <i class="fa-solid fa-download mr-1"></i> Download
            </a>
            <button type="button" class="dark-btn text-xs px-2 py-1"
                    (click)="pick_signed_pdf(view_agreement_row, signedPdfInput)"
                    [disabled]="signed_pdf_busy"
                    title="Replace the signed PDF with a new upload">
              <i class="fa-solid fa-rotate mr-1"></i> Replace
            </button>
            <button type="button" class="dark-btn text-xs px-2 py-1 text-red-500"
                    (click)="remove_signed_pdf(view_agreement_row)"
                    [disabled]="signed_pdf_busy"
                    title="Remove the signed PDF (keeps the agreement row)">
              <i class="fa-solid fa-trash mr-1"></i> Remove
            </button>
          </div>
        </div>
        <ng-template #uploadPdfBlock>
          <div class="flex items-center gap-3 p-2 rounded border border-dashed border-gray-300 dark:border-[#434863] text-[12px]">
            <span class="text-gray-500">No signed PDF attached.</span>
            <button type="button" class="ml-auto dark-btn text-xs px-2 py-1"
                    (click)="pick_signed_pdf(view_agreement_row, signedPdfInput)"
                    [disabled]="signed_pdf_busy">
              <i class="fa-solid fa-upload mr-1"></i> Upload PDF
            </button>
          </div>
        </ng-template>
        <input #signedPdfInput type="file" accept="application/pdf" class="hidden"
               (change)="on_signed_pdf_selected($event, view_agreement_row)" />
        <p *ngIf="signed_pdf_busy" class="text-[11px] text-gray-500 mt-1">
          <i class="fa-solid fa-spinner fa-spin mr-1"></i> Working…
        </p>
      </section>

      <!-- Reason (per-row, used by the single-row Approve/Decline buttons
           in this modal footer only. The top bulk-action bar has its
           own reason input for bulk operations).
           IT-F2-413 c/be868ff8: autofocus so the operator lands on the
           input; red ring appears on failed Decline w/ empty reason. -->
      <section>
        <h4 class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Reason (required for decline)</h4>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs"
               [class.ring-2]="reason_error_flash"
               [class.ring-red-500]="reason_error_flash"
               [class.border-red-500]="reason_error_flash"
               [ngModel]="get_review_reason(view_agreement_row)"
               (ngModelChange)="set_review_reason(view_agreement_row, $event)"
               [disabled]="is_review_busy(view_agreement_row)"
               autofocus
               placeholder="Optional for approve, required for decline" />
      </section>

    </div>

    <!-- Footer: Close + Copy JSON + inline Approve-as-Pro / Approve-as-Non-Pro / Decline.
         All 3 action buttons shown regardless of current status; the
         button matching current state is disabled. Single-row semantics
         preserved here so opening the modal is a full alternative to
         selecting one row in the table and using the top bulk bar. -->
    <div class="px-4 py-3 border-t border-gray-200 dark:border-[#434863] flex flex-wrap justify-end gap-2">
      <button class="dark-btn" (click)="close_view_application()">Close</button>
      <button class="dark-btn"
              (click)="copy_agreement_json()"
              title="Copy the full agreement row as pretty-printed JSON">Copy JSON</button>
      <!-- Approve / Decline hidden when viewing a historical snapshot —
           decisions only apply to the current submission. -->
      <ng-container *ngIf="submission_history_index === 0">
        <button class="dark-btn bg-blue-600 text-white disabled:opacity-50"
                (click)="on_approve_review_as_pro(view_agreement_row)"
                [disabled]="is_approve_pro_disabled(view_agreement_row)"
                [title]="is_approve_pro_disabled(view_agreement_row) && !is_review_busy(view_agreement_row) ? 'Already approved as Pro' : 'Approve as Pro'">Approve as Pro</button>
        <button class="dark-btn bg-green-600 text-white disabled:opacity-50"
                (click)="on_approve_review_as_non_pro(view_agreement_row)"
                [disabled]="is_approve_non_pro_disabled(view_agreement_row)"
                [title]="is_approve_non_pro_disabled(view_agreement_row) && !is_review_busy(view_agreement_row) ? 'Already approved as Non-Pro' : 'Approve as Non-Pro'">Approve as Non-Pro</button>
        <button class="dark-btn text-red-500 disabled:opacity-50"
                (click)="on_decline_review(view_agreement_row)"
                [disabled]="is_decline_disabled(view_agreement_row)"
                [title]="is_decline_disabled(view_agreement_row) && !is_review_busy(view_agreement_row) ? 'Already declined' : (view_agreement_row.REVIEW_STATUS === 'approved' ? 'Decline — REVOKES live data access' : 'Decline')">Decline</button>
      </ng-container>
    </div>
  </div>
</div>
